import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlert } from './AlertContext';
import Button from './Buttons';
import {
  CustomInput,
  CustomNumberInput,
  CustomSelect,
  CustomTextarea,
  CustomToggle,
} from './CustomFormElements';
import PageLoader from './PageLoader';
import { useOrganizationApi } from '../Services/OrganizationApiProvider';
import { phoneCountries } from '../constants/phoneCountries';

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
const languageNames = new Intl.DisplayNames(['en'], { type: 'language' });

const countryOptions = phoneCountries
  .map(({ value }) => ({ value, label: `${regionNames.of(value)} (${value})` }))
  .sort((a, b) => a.label.localeCompare(b.label));

const languageOptions = [
  'uk-UA',
  'en-US',
  'en-GB',
  'pl-PL',
  'ro-RO',
  'ro-MD',
  'de-DE',
  'fr-FR',
  'es-ES',
  'it-IT',
  'pt-PT',
  'cs-CZ',
  'sk-SK',
  'hu-HU',
  'lt-LT',
  'lv-LV',
  'et-EE',
].map((value) => ({ value, label: `${languageNames.of(value)} (${value})` }));

function OrganizationSettingsTab({ organizationId, onSaved, onCancel }) {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { getOrganizationById, updateOrganization } = useOrganizationApi();

  const [loading, setLoading] = useState(true);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isBilled, setIsBilled] = useState(false);
  const [activeCommunications, setActiveCommunications] = useState(0);
  const [communicationsPerDay, setCommunicationsPerDay] = useState(0);
  const [communicationsPerMonth, setCommunicationsPerMonth] = useState(0);
  const [countryCode, setCountryCode] = useState('');
  const [languageCode, setLanguageCode] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getOrganizationById(organizationId, true);

        if (data) {
          setName(data.title || data.name || '');
          setDescription(data.description || '');
          setIsActive(data.is_active ?? true);
          setIsBilled(data.is_billed ?? false);
          setActiveCommunications(data.active_communications || 0);
          setCommunicationsPerDay(data.communications_per_day || 0);
          setCommunicationsPerMonth(data.communications_per_month || 0);
          setCountryCode(data.country_code || '');
          setLanguageCode(data.language_code || '');
        } else {
          navigate('/404');
        }
      } catch (error) {
        console.error('Error fetching organization:', error);
        showAlert('fail', 'Failed to load organization data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organizationId, navigate]);

  const onSave = async () => {
    const data = {
      description: description,
      is_active: isActive,
      is_billed: isBilled,
      active_communications: parseInt(activeCommunications),
      communications_per_day: parseInt(communicationsPerDay),
      communications_per_month: parseInt(communicationsPerMonth),
      country_code: countryCode || null,
      language_code: languageCode || null,
    };

    try {
      setLoading(true);
      await updateOrganization(organizationId, data);
      showAlert('success', 'Organization updated successfully');
      onSaved();
    } catch (error) {
      console.error('Error updating organization:', error);
      showAlert('fail', 'Failed to update organization');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="col-6 radius-yes p-2 bg-yes">
      <h2 className="h2-title">{`Edit Organization: ${name}`}</h2>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Name</div>
          <div className="small-font">Organization Name</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomInput id="org-name" label="Name" type="text" value={name} disabled={true} />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Description</div>
          <div className="small-font">Enter organization description</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomTextarea
            id="description"
            addedText={description}
            onChange={(value) => setDescription(value)}
            resizable={false}
            height="150px"
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Active Communications</div>
          <div className="small-font">Set active communications</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomNumberInput
            id="active-communications"
            value={activeCommunications}
            onChange={(value) => setActiveCommunications(value)}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Communications Per Day</div>
          <div className="small-font">Set communications per day</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomNumberInput
            id="comms-per-day"
            value={communicationsPerDay}
            onChange={(value) => setCommunicationsPerDay(value)}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Communications Per Month</div>
          <div className="small-font">Set communications per month</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomNumberInput
            id="comms-per-month"
            value={communicationsPerMonth}
            onChange={(value) => setCommunicationsPerMonth(value)}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Country</div>
          <div className="small-font">Organization country</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSelect
            id="org-country"
            label="Country"
            options={countryOptions}
            value={countryCode}
            onChange={(val) => setCountryCode(val)}
            placeholder="Select country"
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Language</div>
          <div className="small-font">Organization language</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSelect
            id="org-language"
            label="Language"
            options={languageOptions}
            value={languageCode}
            onChange={(val) => setLanguageCode(val)}
            placeholder="Select language"
          />
        </div>
      </div>

      <div className="input-row input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Organization Status</div>
          <div className="small-font">Enable or disable this organization</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomToggle
            id="toggle-status"
            label={isActive ? 'Active' : 'Inactive'}
            checked={isActive}
            onChange={(id, value) => setIsActive(value)}
          />
        </div>
      </div>

      <div className="input-row input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Billing</div>
          <div className="small-font">Enable or disable billing for this organization</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomToggle
            id="toggle-billing"
            label={isBilled ? 'Billed' : 'Not billed'}
            checked={isBilled}
            onChange={(id, value) => setIsBilled(value)}
          />
        </div>
      </div>

      <div className="buttons-container justify-end gap-12">
        <Button background="color2" size="medium" onClick={onSave}>
          Save
        </Button>
        <Button variant="danger" size="medium" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default OrganizationSettingsTab;
